import { useEndpointTestRunScores } from '@/hooks/useEndpointTestEvals';
import { scoreCriterion } from '@/utils/testRunResults';
import type { EndpointTestRunScore } from '@/types/endpointTests';
import { passThresholdOf } from './CriteriaEditor';

/** Failures first, then uncertain, then passes (task 1180). */
const OUTCOME_ORDER: Record<string, number> = { fail: 0, uncertain: 1, pass: 2 };
/** Within an outcome: checks, graded criteria, then the human verdict. */
const KIND_ORDER: Record<string, number> = { check: 0, graded: 1, human: 2 };

export function sortScores(scores: EndpointTestRunScore[]): EndpointTestRunScore[] {
  return scores
    .map((score, index) => ({ score, index }))
    .sort(
      (a, b) =>
        (OUTCOME_ORDER[a.score.outcome] ?? 3) - (OUTCOME_ORDER[b.score.outcome] ?? 3) ||
        (KIND_ORDER[a.score.kind] ?? 3) - (KIND_ORDER[b.score.kind] ?? 3) ||
        a.index - b.index,
    )
    .map((entry) => entry.score);
}

const outcomeClass = (outcome: string) =>
  outcome === 'fail'
    ? 'text-red-600 dark:text-red-400'
    : outcome === 'uncertain'
      ? 'text-amber-600 dark:text-amber-400'
      : 'text-green-700 dark:text-green-400';

/**
 * A graded row's tooltip names the bar its score is judged against (task 1265),
 * so a reader sees why 0.65 failed. Read from the criterion's definition on the
 * scores route; checks and the human verdict have no bar.
 */
const barTitle = (score: EndpointTestRunScore): string | undefined => {
  if (score.kind !== 'graded') return undefined;
  const definition = score.criterion?.definition;
  return definition ? `bar ${passThresholdOf(definition)}` : undefined;
};

/**
 * Every score on one result (task 1180): one row per check, graded criterion
 * and the human verdict — outcome, score, the grader's one-line rationale (or
 * the reviewer's note), and a `cached` marker when the grade came from the
 * grader cache. Graded criteria always carry a 0 to 1 score (task 1265).
 */
export function ScoresTable({ endpointTestRunId, revision }: { endpointTestRunId: string; revision?: string }) {
  const { data, isLoading, isError } = useEndpointTestRunScores(endpointTestRunId, revision);
  if (isLoading) return <p className="text-xs text-gray-500 dark:text-gray-400">Loading criteria…</p>;
  if (isError) return <p className="text-xs text-red-500">Couldn’t load this result’s criteria.</p>;
  const scores = sortScores(data ?? []);
  if (scores.length === 0) return null;

  return (
    <div className="overflow-x-auto" data-testid="scores-table">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-left text-gray-500 dark:text-gray-400">
            <th className="py-1 pr-3 font-medium">Criterion</th>
            <th className="py-1 pr-3 font-medium">Result</th>
            <th className="py-1 font-medium">Rationale</th>
          </tr>
        </thead>
        <tbody>
          {scores.map((score) => (
            <tr key={score.id} data-testid={`score-row-${score.id}`} title={barTitle(score)} className="border-t border-gray-100 dark:border-gray-700 align-top">
              <td className="py-1.5 pr-3 text-gray-900 dark:text-white">
                {score.kind === 'human' ? (
                  <span className="inline-flex items-center gap-1">
                    Human verdict
                    <span className="px-1 rounded text-[10px] font-medium uppercase bg-indigo-100 text-indigo-700 dark:bg-indigo-900 dark:text-indigo-200">human</span>
                  </span>
                ) : (
                  <>
                    {scoreCriterion(score)?.name ?? 'Criterion'}
                    <span className="ml-1 text-gray-400">
                      · {score.kind}
                      {scoreCriterion(score)?.required === false ? ' · informational' : ''}
                    </span>
                  </>
                )}
              </td>
              <td className={`py-1.5 pr-3 whitespace-nowrap ${outcomeClass(score.outcome)}`}>
                {score.outcome}
                {typeof score.score === 'number' && ` · ${score.score.toFixed(2)}`}
                {score.cached && (
                  <span className="ml-1.5 px-1 rounded text-[10px] bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">cached</span>
                )}
              </td>
              <td className="py-1.5 text-gray-600 dark:text-gray-300">{score.rationale ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default ScoresTable;
